import React, { useState, useEffect, useMemo } from "react";
import DateFilter from "./DateFilter";
import TopSummaryCards from "./TopSummaryCards";
import RevenueOverview from "./RevenueOverview";
import ChartsSection from "./ChartsSection";
import FinanceSummary from "./FinanceSummary";
import TopRevenueList from "./TopRevenueList";

// Same 6-month limit as DateFilter and InstallationSummaryReport
import { getAuthCookie } from "../../utils/authCookies";
import { DataTableLoadingIndicator } from "components/DataTableLoader";

const getDateRangeLimits = () => {
  const today = new Date();
  const sixMonthsAgo = new Date();
  sixMonthsAgo.setMonth(today.getMonth() - 6);
  const formatDate = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };
  return {
    minDate: formatDate(sixMonthsAgo),
    maxDate: formatDate(today),
  };
};

interface DashboardMainProps {
  baseRoute?: string;
}

const DashboardMain = ({ baseRoute = "/signage" }: DashboardMainProps) => {
  const signageId = getAuthCookie("userId");
  const organizationId = getAuthCookie("organizationId");
  const token = getAuthCookie("sessionToken");

  const dateRangeLimits = useMemo(getDateRangeLimits, []);

  // date filter (custom range limited to 6 months via DateFilter)
  const [filter, setFilter] = useState({
    filterType: "last_90_days",
    fromDate: "",
    toDate: "",
  });

  // NEW state to store selected range text
  const [dateLabel, setDateLabel] = useState("Last 90 Days");

  const [summaryData, setSummaryData] = useState(null);
  const [financeData, setFinanceData] = useState(null);
  const [loading, setLoading] = useState(true);

  // const handleFilterChange = (newFilter) => {
  //   setFilter(newFilter);
  // };

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (
        filter.filterType === "custom" &&
        (!filter.fromDate || !filter.toDate)
      ) {
        return;
      }

      setLoading(true);
      try {
        let baseUrl = `${process.env.REACT_APP_API_URL}/dashboard/organizations`;
        let summaryUrl = `${baseUrl}/${organizationId}/summary?filter_type=${filter.filterType}`;
        let financeUrl = `${baseUrl}/${organizationId}/finance-dashboard?filter_type=${filter.filterType}`;

        if (filter.filterType === "custom") {
          // Clamp to 6-month window (same as InstallationSummaryReport)
          const from =
            filter.fromDate &&
            (filter.fromDate < dateRangeLimits.minDate || filter.fromDate > dateRangeLimits.maxDate)
              ? dateRangeLimits.minDate
              : filter.fromDate;
          const to =
            filter.toDate &&
            (filter.toDate < dateRangeLimits.minDate || filter.toDate > dateRangeLimits.maxDate)
              ? dateRangeLimits.maxDate
              : filter.toDate;
          if (from && to) {
            const range = `&from=${from}&to=${to}`;
            summaryUrl += range;
            financeUrl += range;
          }
        }

        const [summaryRes, financeRes] = await Promise.all([
          fetch(summaryUrl, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }),
          fetch(financeUrl, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }),
        ]);

        const [summaryJson, financeJson] = await Promise.all([
          summaryRes.json(),
          financeRes.json(),
        ]);

        setSummaryData(summaryJson);
        setFinanceData(financeJson);
      } catch (err) {
        console.error("Error loading dashboard:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [filter, signageId, token, dateRangeLimits]);

  if (loading) {
    return (
      <div className="text-center py-5 d-flex flex-column align-items-center">
        <DataTableLoadingIndicator message="Loading dashboard…" />
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <DateFilter
        filter={filter}
        onFilterChange={setFilter}
        onRangeLabel={(label:any) => setDateLabel(label)}   // <-- FIXED
      />

      <TopSummaryCards summaryData={summaryData} baseRoute={baseRoute} />

      <RevenueOverview financeData={financeData} baseRoute={baseRoute} />

      <ChartsSection financeData={financeData}/>

      <FinanceSummary financeData={financeData|| {}} dateRangeLabel={dateLabel} /> 
      {/* FIXED */}

      <TopRevenueList financeData={financeData} baseRoute={baseRoute} />
    </div>
  );
};

export default DashboardMain;
